import React, { useState, useEffect, useRef } from "react";
import { EmailVariable, VariableContext } from "../../types/workflow";
import { workflowService } from "../../services/workflowService";

interface EmailVariablePickerProps {
  onVariableSelect: (variable: EmailVariable) => void;
  onClose: () => void;
  context?: VariableContext;
  trigger?: HTMLElement | null;
  position?: { x: number; y: number };
}

const EmailVariablePicker: React.FC<EmailVariablePickerProps> = ({
  onVariableSelect,
  onClose,
  context,
  trigger,
  position,
}) => {
  const [variables, setVariables] = useState<EmailVariable[]>([]);
  const [filteredVariables, setFilteredVariables] = useState<EmailVariable[]>(
    []
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const pickerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Sample variables (in real app, these would come from API)
  const sampleVariables: EmailVariable[] = [
    {
      id: "user_name",
      name: "user.name",
      label: "User Name",
      type: "text",
      source: "user_input",
      category: "contacts",
      description: "The name of the current user",
      example_value: "John Doe",
      is_dynamic: false,
    },
    {
      id: "user_email",
      name: "user.email",
      label: "User Email",
      type: "text",
      source: "user_input",
      category: "contacts",
      description: "The email address of the current user",
      example_value: "john@example.com",
      is_dynamic: false,
    },
    {
      id: "jira_bugs_fixed",
      name: "jira.bugs_fixed",
      label: "Bugs Fixed (Weekly)",
      type: "number",
      source: "integration",
      category: "metrics",
      description: "Number of bugs fixed in the past week from Jira",
      example_value: "12",
      integration_source: "jira",
      is_dynamic: true,
      last_updated: "2024-01-15T10:30:00Z",
    },
    {
      id: "github_commits",
      name: "github.commits_count",
      label: "Weekly Commits",
      type: "number",
      source: "integration",
      category: "metrics",
      description: "Total commits made in the past week",
      example_value: "23",
      integration_source: "github",
      is_dynamic: true,
      last_updated: "2024-01-15T10:30:00Z",
    },
    {
      id: "asana_tasks_completed",
      name: "asana.tasks_completed",
      label: "Tasks Completed",
      type: "number",
      source: "integration",
      category: "tasks",
      description: "Tasks completed in Asana this week",
      example_value: "8",
      integration_source: "asana",
      is_dynamic: true,
      last_updated: "2024-01-15T10:30:00Z",
    },
    {
      id: "team_velocity",
      name: "calculated.team_velocity",
      label: "Team Velocity",
      type: "number",
      source: "calculation",
      category: "metrics",
      description: "Calculated team velocity based on completed work",
      example_value: "85%",
      calculation_method: "story_points_completed / story_points_planned",
      is_dynamic: true,
      last_updated: "2024-01-15T10:30:00Z",
    },
    {
      id: "current_date",
      name: "system.current_date",
      label: "Current Date",
      type: "date",
      source: "calculation",
      category: "custom",
      description: "The current date when the email is sent",
      example_value: "January 15, 2024",
      is_dynamic: true,
    },
    {
      id: "project_status",
      name: "projects.current_status",
      label: "Project Status",
      type: "text",
      source: "integration",
      category: "projects",
      description: "Current status of the main project",
      example_value: "On Track",
      integration_source: "asana",
      is_dynamic: true,
      last_updated: "2024-01-15T10:30:00Z",
    },
  ];

  useEffect(() => {
    // In real app, load variables from API
    setLoading(true);
    setTimeout(() => {
      setVariables(sampleVariables);
      setFilteredVariables(sampleVariables);
      setLoading(false);
    }, 300);
  }, []);

  useEffect(() => {
    // Filter variables based on search and category
    let filtered = variables;

    if (searchTerm) {
      filtered = filtered.filter(
        (variable) =>
          variable.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          variable.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          variable.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory !== "all") {
      filtered = filtered.filter(
        (variable) => variable.category === selectedCategory
      );
    }

    setFilteredVariables(filtered);
    setSelectedIndex(0);
  }, [searchTerm, selectedCategory, variables]);

  useEffect(() => {
    // Focus search input when component mounts
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    // Handle keyboard navigation
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < filteredVariables.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case "Enter":
          e.preventDefault();
          if (filteredVariables[selectedIndex]) {
            handleVariableSelect(filteredVariables[selectedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [filteredVariables, selectedIndex, onClose]);

  useEffect(() => {
    // Handle clicks outside the picker
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleVariableSelect = (variable: EmailVariable) => {
    onVariableSelect(variable);
    onClose();
  };

  const categories = [
    { value: "all", label: "All Variables", icon: "📋" },
    { value: "metrics", label: "Metrics", icon: "📊" },
    { value: "contacts", label: "Contacts", icon: "👥" },
    { value: "projects", label: "Projects", icon: "📁" },
    { value: "tasks", label: "Tasks", icon: "✅" },
    { value: "custom", label: "Custom", icon: "⚙️" },
  ];

  const getVariableIcon = (variable: EmailVariable) => {
    switch (variable.type) {
      case "text":
        return "📝";
      case "number":
        return "🔢";
      case "date":
        return "📅";
      case "object":
        return "📦";
      case "array":
        return "📚";
      default:
        return "📄";
    }
  };

  const getSourceBadgeColor = (source: string) => {
    switch (source) {
      case "integration":
        return "bg-blue-100 text-blue-800";
      case "calculation":
        return "bg-purple-100 text-purple-800";
      case "user_input":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPickerPosition = (): React.CSSProperties => {
    if (position) {
      return {
        position: "fixed",
        top: position.y,
        left: position.x,
        zIndex: 1000,
      };
    }

    if (trigger) {
      const rect = trigger.getBoundingClientRect();
      return {
        position: "fixed",
        top: rect.bottom + 8,
        left: rect.left,
        zIndex: 1000,
      };
    }

    return {
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      zIndex: 1000,
    };
  };

  return (
    <div
      ref={pickerRef}
      className="bg-white border border-gray-200 rounded-lg shadow-lg w-96 max-h-96 overflow-hidden"
      style={getPickerPosition()}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-900">Insert Variable</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <span className="sr-only">Close</span>
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Search */}
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search variables..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>

      {/* Categories */}
      <div className="px-4 py-2 border-b border-gray-200 bg-gray-50">
        <div className="flex space-x-1 overflow-x-auto">
          {categories.map((category) => (
            <button
              key={category.value}
              onClick={() => setSelectedCategory(category.value)}
              className={`flex items-center space-x-1 px-2 py-1 text-xs font-medium rounded-md whitespace-nowrap transition-colors ${
                selectedCategory === category.value
                  ? "bg-indigo-100 text-indigo-800"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <span>{category.icon}</span>
              <span>{category.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Variables List */}
      <div className="max-h-64 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
          </div>
        ) : filteredVariables.length === 0 ? (
          <div className="px-4 py-8 text-center text-gray-500">
            <div className="text-2xl mb-2">🔍</div>
            <div className="text-sm">No variables found</div>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="mt-2 text-xs text-indigo-600 hover:text-indigo-500"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="py-1">
            {filteredVariables.map((variable, index) => (
              <button
                key={variable.id}
                onClick={() => handleVariableSelect(variable)}
                className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                  index === selectedIndex
                    ? "bg-indigo-50 border-r-2 border-indigo-500"
                    : ""
                }`}
              >
                <div className="flex items-start space-x-3">
                  <span className="text-lg mt-0.5">
                    {getVariableIcon(variable)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {variable.label}
                      </span>
                      {variable.is_dynamic && (
                        <span
                          className="flex-shrink-0 w-2 h-2 bg-green-400 rounded-full"
                          title="Dynamic variable"
                        ></span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 mb-1">
                      <code className="text-xs font-mono text-gray-600 bg-gray-100 px-1 rounded">
                        {variable.name}
                      </code>
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getSourceBadgeColor(
                          variable.source
                        )}`}
                      >
                        {variable.source === "integration"
                          ? variable.integration_source
                          : variable.source}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 line-clamp-2">
                      {variable.description}
                    </p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-gray-400">
                        Example: {variable.example_value}
                      </span>
                      {variable.last_updated && (
                        <span className="text-xs text-gray-400">
                          Updated{" "}
                          {new Date(variable.last_updated).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>
            {filteredVariables.length} variable
            {filteredVariables.length !== 1 ? "s" : ""}
          </span>
          <div className="flex items-center space-x-4">
            <span>↑↓ Navigate</span>
            <span>↵ Select</span>
            <span>Esc Close</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Variable Display Component (for showing variables in the editor)
export interface VariableNodeProps {
  variable: EmailVariable;
  value?: any;
  isEditable?: boolean;
  onEdit?: () => void;
}

export const VariableNode: React.FC<VariableNodeProps> = ({
  variable,
  value,
  isEditable = false,
  onEdit,
}) => {
  const displayValue = value !== undefined ? value : variable.example_value;

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-md text-sm font-medium ${
        variable.is_dynamic
          ? "bg-blue-100 text-blue-800 border border-blue-200"
          : "bg-gray-100 text-gray-800 border border-gray-200"
      } ${isEditable ? "cursor-pointer hover:bg-opacity-80" : ""}`}
      onClick={isEditable ? onEdit : undefined}
      title={`${variable.description} (${variable.name})`}
    >
      <span className="mr-1">{variable.is_dynamic ? "🔄" : "📌"}</span>
      <span>{displayValue}</span>
      {isEditable && <span className="ml-1 text-xs opacity-60">✏️</span>}
    </span>
  );
};

// Service function to process variables in text
export const processVariablesInText = (
  text: string,
  variables: EmailVariable[],
  context?: VariableContext
): string => {
  let processedText = text;

  variables.forEach((variable) => {
    const pattern = new RegExp(`{{\\s*${variable.name}\\s*}}`, "g");
    const value =
      context?.custom_variables?.[variable.name] || variable.example_value;
    processedText = processedText.replace(pattern, String(value));
  });

  return processedText;
};

export default EmailVariablePicker;
